import { GlobalLimits, IHasOrgId, IHasSchedule, IHasSettings, IWarehouse } from './';
import { INovaEntity, NovaId } from './base';
import type { Maybe } from './globals';
import { iEqual } from './js-helpers/str';
import { IOrg } from './org';

export enum LoadTypeDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
  InOut = 'Inbound/Outbound',
  Load = 'Load',
  Unload = 'Unload'
}

export enum LoadTypeOperation {
  Live = 'Live',
  Drop = 'Drop',
  Other = 'Other'
}

export enum LoadTypeEquipmentType {
  DryVan = 'Dry Van',
  Flatbed = 'Flatbed',
  Reefer = 'Reefer',
  Trailer = 'Trailer',
  Container = 'Container',
  Other = 'Other'
}

export enum LoadTypeTransportationMode {
  FTL = 'FTL',
  PTL = 'PTL',
  Other = 'Other'
}

export enum LoadTypeType {
  Standard = 'Standard',
  Preparation = 'Preparation'
}

export const MAX_LOAD_TYPES_PER_WAREHOUSE = GlobalLimits.MAX_LOAD_TYPES_PER_WAREHOUSE.value;

export interface ILoadType extends INovaEntity, IHasOrgId, IHasSettings, IHasSchedule {
  name: string;
  duration_min: number;
  orgId: string;
  org?: Maybe<IOrg>;
  warehouseId?: Maybe<NovaId>;
  warehouse?: Maybe<IWarehouse>;
  direction: LoadTypeDirection;
  operation?: Maybe<LoadTypeOperation>;
  equipmentType?: Maybe<LoadTypeEquipmentType>;
  transportationMode?: Maybe<LoadTypeTransportationMode>;
  type: LoadTypeType;
  allowCarrierScheduling: boolean;
  description?: Maybe<string>;
}

export function isStandardLoadType(loadtype: ILoadType): boolean {
  return loadtype.type === LoadTypeType.Standard;
}

export function isPreparationLoadType(loadtype: ILoadType): boolean {
  return loadtype.type === LoadTypeType.Preparation;
}

// LoadTypeDirection/LoadTypeEquipmentType values that only apply to Preparation load types,
// gated behind the enable-preparation-appointments feature flag.
export const PREPARATION_ONLY_LOAD_TYPE_DIRECTIONS: LoadTypeDirection[] = [
  LoadTypeDirection.Load,
  LoadTypeDirection.Unload
];

export const PREPARATION_ONLY_LOAD_TYPE_EQUIPMENT_TYPES: LoadTypeEquipmentType[] = [
  LoadTypeEquipmentType.Trailer,
  LoadTypeEquipmentType.Container
];

export function isPreparationOnlyLoadTypeDirection(direction: LoadTypeDirection): boolean {
  return PREPARATION_ONLY_LOAD_TYPE_DIRECTIONS.includes(direction);
}

export function isPreparationOnlyLoadTypeEquipmentType(
  equipmentType: LoadTypeEquipmentType
): boolean {
  return PREPARATION_ONLY_LOAD_TYPE_EQUIPMENT_TYPES.includes(equipmentType);
}

// Load types a container pickup can be booked on. Only these trigger the driver's
// pickup-location message on arrival.
export function isPickupLoadType(
  loadtype: Maybe<Pick<ILoadType, 'operation' | 'direction'>>
): boolean {
  return (
    loadtype?.operation === LoadTypeOperation.Drop &&
    [LoadTypeDirection.Outbound, LoadTypeDirection.InOut].includes(loadtype.direction)
  );
}

export function isWarehouseLoadType(loadtype: ILoadType): boolean {
  return Boolean(loadtype.warehouseId);
}

// i18n keys for how a load type direction is displayed. Drop load types read as
// Drop off / Pick up / Drop & hook; every other combined direction reads "Inbound & outbound".
export enum LoadTypeDirectionDisplay {
  Inbound = 'inbound',
  Outbound = 'outbound',
  Load = 'load',
  Unload = 'unload',
  DropOff = 'dropOff',
  PickUp = 'pickUp',
  DropAndHook = 'dropAndHook',
  InboundAndOutbound = 'inboundAndOutbound'
}

const DEFAULT_DIRECTION_DISPLAYS: Record<LoadTypeDirection, LoadTypeDirectionDisplay> = {
  [LoadTypeDirection.Inbound]: LoadTypeDirectionDisplay.Inbound,
  [LoadTypeDirection.Outbound]: LoadTypeDirectionDisplay.Outbound,
  [LoadTypeDirection.InOut]: LoadTypeDirectionDisplay.InboundAndOutbound,
  [LoadTypeDirection.Load]: LoadTypeDirectionDisplay.Load,
  [LoadTypeDirection.Unload]: LoadTypeDirectionDisplay.Unload
};

const OPERATION_DIRECTION_DISPLAYS: Partial<
  Record<LoadTypeOperation, Partial<Record<LoadTypeDirection, LoadTypeDirectionDisplay>>>
> = {
  [LoadTypeOperation.Drop]: {
    [LoadTypeDirection.Inbound]: LoadTypeDirectionDisplay.DropOff,
    [LoadTypeDirection.Outbound]: LoadTypeDirectionDisplay.PickUp,
    [LoadTypeDirection.InOut]: LoadTypeDirectionDisplay.DropAndHook
  }
};

const SHORT_DIRECTION_LABELS: Partial<Record<LoadTypeDirectionDisplay, string>> = {
  [LoadTypeDirectionDisplay.Inbound]: 'IN',
  [LoadTypeDirectionDisplay.Outbound]: 'OUT',
  [LoadTypeDirectionDisplay.InboundAndOutbound]: 'IN&OUT',
  [LoadTypeDirectionDisplay.DropOff]: 'DO',
  [LoadTypeDirectionDisplay.PickUp]: 'PU',
  [LoadTypeDirectionDisplay.DropAndHook]: 'D&H'
};

function findEnumValue<Value extends string>(
  enumValues: Value[],
  candidate: Maybe<string>
): Maybe<Value> {
  if (!candidate) {
    return null;
  }

  return enumValues.find(enumValue => iEqual(enumValue, candidate)) ?? null;
}

export interface LoadTypeDirectionSubject {
  direction?: Maybe<LoadTypeDirection>;
  operation?: Maybe<LoadTypeOperation>;
}

export function getLoadTypeDirectionDisplay(
  loadtype: Maybe<LoadTypeDirectionSubject>
): Maybe<LoadTypeDirectionDisplay> {
  const direction = findEnumValue(Object.values(LoadTypeDirection), loadtype?.direction);

  if (!direction) {
    return null;
  }

  const operation = findEnumValue(Object.values(LoadTypeOperation), loadtype?.operation);

  return (
    (operation && OPERATION_DIRECTION_DISPLAYS[operation]?.[direction]) ||
    DEFAULT_DIRECTION_DISPLAYS[direction]
  );
}

export function getLoadtypeShortDirection(
  loadtype: Maybe<LoadTypeDirectionSubject>,
  useOperationLabels = false
): string {
  if (!loadtype?.direction) {
    return '';
  }

  const display = getLoadTypeDirectionDisplay({
    direction: loadtype.direction,
    operation: useOperationLabels ? loadtype.operation : null
  });

  return (display && SHORT_DIRECTION_LABELS[display]) || loadtype.direction;
}
